import { logger as SentryLogger } from '@/shared/helpers/logger';
import type { IdTokenClaims } from '@logto/react';
import { useLogto } from '@logto/react';
import { useEffect } from 'react';

const ID_TOKEN_CLAIMS_TIMEOUT_MS = 8000;
const UNAUTHENTICATED_CLAIMS_PROMISE: Promise<IdTokenClaims | undefined> =
  Promise.resolve(undefined);
let cachedIdTokenClaimsPromise: Promise<IdTokenClaims | undefined> | null = null;
let resolveCachedIdTokenClaims: ((value: IdTokenClaims | undefined) => void) | null = null;

async function withClaimsTimeout(
  claimsPromise: Promise<IdTokenClaims | undefined>,
): Promise<IdTokenClaims | undefined> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<undefined>((resolve) => {
    timeoutId = setTimeout(() => {
      SentryLogger.warn('Timed out while loading id token claims', {
        log_source: 'useIdTokenClaimsPromise',
      });
      resolve(undefined);
    }, ID_TOKEN_CLAIMS_TIMEOUT_MS);
  });

  const safeClaimsPromise = claimsPromise.catch((error: unknown) => {
    SentryLogger.error('Error loading id token claims', {
      log_source: 'useIdTokenClaimsPromise',
      error,
    });
    return undefined;
  });

  const result = await Promise.race([safeClaimsPromise, timeoutPromise]);

  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  return result;
}

function ensureCachedIdTokenClaimsPromise(): Promise<IdTokenClaims | undefined> {
  if (cachedIdTokenClaimsPromise) return cachedIdTokenClaimsPromise;
  cachedIdTokenClaimsPromise = new Promise<IdTokenClaims | undefined>((resolve) => {
    resolveCachedIdTokenClaims = resolve;
  });
  return cachedIdTokenClaimsPromise;
}

export function useIdTokenClaimsPromise() {
  const { getIdTokenClaims, isAuthenticated } = useLogto();
  const claimsPromise = isAuthenticated
    ? ensureCachedIdTokenClaimsPromise()
    : UNAUTHENTICATED_CLAIMS_PROMISE;

  useEffect(() => {
    if (!isAuthenticated) {
      if (resolveCachedIdTokenClaims) {
        resolveCachedIdTokenClaims(undefined);
        resolveCachedIdTokenClaims = null;
      }
      cachedIdTokenClaimsPromise = null;
      return;
    }

    if (!resolveCachedIdTokenClaims) return;
    const resolve = resolveCachedIdTokenClaims;
    resolveCachedIdTokenClaims = null;
    void withClaimsTimeout(getIdTokenClaims()).then(resolve);
  }, [isAuthenticated, getIdTokenClaims]);

  return {
    claimsPromise,
  };
}
